import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
} from "@mui/material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { SwayyOrderDetailItem } from "../../../../../models/SwayyOrder/SwayyOrderDetail";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { SwayyCarrier } from "../../../../../models/SwayyCarrier/SwayyCarrier";
import { SwayyWarehouse } from "../../../../../models/SwayyWarehouse/SwayyWarehouse";
import { CreateSwayyShipmentPayload } from "../../../../../models/SwayyShipment/SwayyShipment";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

interface SwayyCreateShipmentDialogProps {
  open: boolean;
  items: SwayyOrderDetailItem[];
  warehouses: SwayyWarehouse[];
  carriers: SwayyCarrier[];
  loading?: boolean;
  lookupsLoading?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (payload: CreateSwayyShipmentPayload) => void;
}

const inputSx = {
  borderRadius: swayyRadii.sm,
  fontFamily: swayyFont,
  fontSize: 13,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
};

const toMysqlDatetime = (value: string): string => {
  if (!value) return "";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format("YYYY-MM-DD HH:mm:ss");
};

const SwayyCreateShipmentDialog: React.FC<SwayyCreateShipmentDialogProps> = ({
  open,
  items,
  warehouses,
  carriers,
  loading = false,
  lookupsLoading = false,
  error = null,
  onClose,
  onSubmit,
}) => {
  const shippableItems = useMemo(
    () =>
      items
        .map((item) => ({
          ...item,
          remaining: Math.max(0, item.qty - (item.qty_shipped ?? 0)),
        }))
        .filter((item) => item.remaining > 0),
    [items],
  );

  const [warehouseId, setWarehouseId] = useState<string[]>([]);
  const [carrierId, setCarrierId] = useState<string[]>([]);
  const [expectedAt, setExpectedAt] = useState("");
  const [awb, setAwb] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [selectedQty, setSelectedQty] = useState<Record<number, number>>({});
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setExpectedAt(dayjs().add(3, "day").format("YYYY-MM-DDTHH:mm"));
    setAwb("");
    setTrackingUrl("");
    setLocalError(null);
    const nextSelected: Record<number, boolean> = {};
    const nextQty: Record<number, number> = {};
    shippableItems.forEach((item) => {
      nextSelected[item.id] = true;
      nextQty[item.id] = item.remaining;
    });
    setSelectedIds(nextSelected);
    setSelectedQty(nextQty);
  }, [open, shippableItems]);

  useEffect(() => {
    if (!open) {
      setWarehouseId([]);
      return;
    }
    if (warehouses.length !== 1) return;
    const id = String(warehouses[0].id);
    setWarehouseId((prev) => (prev[0] === id ? prev : [id]));
  }, [open, warehouses]);

  useEffect(() => {
    if (!open) {
      setCarrierId([]);
      return;
    }
    if (carriers.length !== 1) return;
    const id = String(carriers[0].id);
    setCarrierId((prev) => (prev[0] === id ? prev : [id]));
  }, [open, carriers]);

  const warehouseOptions: FilterValue[] = warehouses.map((warehouse) => ({
    id: String(warehouse.id),
    value: `${warehouse.name} (${warehouse.code})`,
  }));

  const carrierOptions: FilterValue[] = carriers.map((carrier) => ({
    id: String(carrier.id),
    value: carrier.name,
  }));

  const selectedWarehouseId = Number(warehouseId[0]);
  const selectedCarrierId = Number(carrierId[0]);
  const canCreate =
    !loading &&
    !lookupsLoading &&
    shippableItems.length > 0 &&
    Boolean(selectedWarehouseId) &&
    Boolean(selectedCarrierId);

  const handleSubmit = () => {
    setLocalError(null);
    if (!selectedWarehouseId) {
      setLocalError("Select a warehouse.");
      return;
    }
    if (!selectedCarrierId) {
      setLocalError("Select a courier.");
      return;
    }
    if (!expectedAt) {
      setLocalError("Expected delivery date is required.");
      return;
    }

    const shipmentItems = shippableItems
      .filter((item) => selectedIds[item.id])
      .map((item) => ({
        order_item_id: item.id,
        qty: Number(selectedQty[item.id] || 0),
      }))
      .filter((item) => item.qty > 0);

    if (shipmentItems.length === 0) {
      setLocalError("Select at least one item with quantity.");
      return;
    }

    for (const item of shipmentItems) {
      const source = shippableItems.find((row) => row.id === item.order_item_id);
      if (!source || item.qty > source.remaining) {
        setLocalError("One or more quantities exceed remaining qty.");
        return;
      }
    }

    onSubmit({
      warehouse_id: selectedWarehouseId,
      expected_at: toMysqlDatetime(expectedAt),
      items: shipmentItems,
      carrier_id: selectedCarrierId,
      awb: awb.trim() || null,
      tracking_url: trackingUrl.trim() || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: swayyRadii.xl,
          border: `1px solid ${swayyColors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: swayyFont,
          fontWeight: 700,
          color: swayyColors.deepGreen,
          pb: 1,
        }}
      >
        Create shipment
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <CustomTypography
          variant="body2"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont, mb: 2 }}
        >
          Creating a shipment fulfills inventory and queues a pending invoice for
          the shipped lines.
        </CustomTypography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <AppSearchDropdownMulti
            title="Select warehouse *"
            isMultiSelect={false}
            list={warehouseOptions}
            value={warehouseOptions.filter((option) =>
              warehouseId.includes(option.id),
            )}
            local
            floatingLabel
            fontSize="12px"
            borderColor={swayyColors.border}
            accentColor={swayyColors.pine}
            backgroundColor={swayyColors.white}
            disabled={loading || lookupsLoading}
            handleSelect={(items) =>
              setWarehouseId(items.map((item) => item.id))
            }
          />
          <AppSearchDropdownMulti
            title="Select courier *"
            isMultiSelect={false}
            list={carrierOptions}
            value={carrierOptions.filter((option) =>
              carrierId.includes(option.id),
            )}
            local
            floatingLabel
            fontSize="12px"
            borderColor={swayyColors.border}
            accentColor={swayyColors.pine}
            backgroundColor={swayyColors.white}
            disabled={loading || lookupsLoading}
            handleSelect={(items) => setCarrierId(items.map((item) => item.id))}
          />
          <OutlinedInput
            type="datetime-local"
            value={expectedAt}
            onChange={(e) => setExpectedAt(e.target.value)}
            disabled={loading}
            fullWidth
            sx={{ ...inputSx, height: 42 }}
          />
          <OutlinedInput
            value={awb}
            onChange={(e) => setAwb(e.target.value)}
            placeholder="AWB (optional)"
            disabled={loading}
            fullWidth
            sx={{ ...inputSx, height: 42 }}
          />
          <OutlinedInput
            value={trackingUrl}
            onChange={(e) => setTrackingUrl(e.target.value)}
            placeholder="Tracking URL (optional)"
            disabled={loading}
            fullWidth
            sx={{ ...inputSx, height: 42, gridColumn: { md: "1 / -1" } }}
          />
        </Box>

        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ fontFamily: swayyFont, fontWeight: 700, mb: 1 }}
        >
          Items to ship
        </CustomTypography>

        {shippableItems.length === 0 ? (
          <CustomTypography
            variant="body2"
            color={swayyColors.textSecondary}
            sx={{ fontFamily: swayyFont }}
          >
            No remaining quantities available to ship.
          </CustomTypography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {shippableItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.25,
                  borderRadius: swayyRadii.md,
                  border: `1px solid ${swayyColors.border}`,
                  bgcolor: swayyColors.pageBg,
                }}
              >
                <Checkbox
                  checked={Boolean(selectedIds[item.id])}
                  onChange={(e) =>
                    setSelectedIds((prev) => ({
                      ...prev,
                      [item.id]: e.target.checked,
                    }))
                  }
                  disabled={loading}
                  sx={{ color: swayyColors.pine }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTypography
                    variant="body2"
                    color={swayyColors.deepGreen}
                    sx={{ fontFamily: swayyFont, fontWeight: 700 }}
                  >
                    {item.name}
                  </CustomTypography>
                  <Box
                    sx={{
                      mt: 0.35,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <SkuCopyRow
                      sku={item.sku}
                      emptyFallback="—"
                      copyLabel="Copy SKU"
                      color={swayyColors.textSecondary}
                      variant="body3"
                      allowWrap
                      typographySx={{
                        fontFamily: swayyFont,
                        fontSize: 12,
                      }}
                    />
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.textSecondary}
                      sx={{ fontFamily: swayyFont, fontSize: 12 }}
                    >
                      · Remaining {item.remaining}
                    </CustomTypography>
                  </Box>
                </Box>
                <OutlinedInput
                  type="number"
                  value={selectedQty[item.id] ?? item.remaining}
                  onChange={(e) =>
                    setSelectedQty((prev) => ({
                      ...prev,
                      [item.id]: Math.max(
                        1,
                        Math.min(item.remaining, Number(e.target.value) || 1),
                      ),
                    }))
                  }
                  disabled={loading || !selectedIds[item.id]}
                  inputProps={{ min: 1, max: item.remaining }}
                  sx={{ ...inputSx, width: 88, height: 40 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {(localError || error) && (
          <CustomTypography
            variant="body2"
            color={swayyColors.danger}
            sx={{ fontFamily: swayyFont, mt: 1.5, whiteSpace: "pre-line" }}
          >
            {localError || error}
          </CustomTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <AppButton
          label="Cancel"
          onClick={onClose}
          disabled={loading}
          customStyles={{
            borderRadius: swayyRadii.md,
            bgcolor: "transparent",
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            "&:hover": { bgcolor: swayyColors.pineLight },
          }}
        />
        <AppButton
          label={loading ? "Creating..." : "Create shipment"}
          onClick={handleSubmit}
          disabled={!canCreate}
          customStyles={{
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.pine,
            color: swayyColors.white,
            "&:hover": { bgcolor: swayyColors.pineHover },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(SwayyCreateShipmentDialog);
